import type { LevelEtaMode } from '@/core/display-settings'
import type { GameStateSnapshot, LiveRateSnapshot, SessionRecord } from '@/domain/types'

/** Match the selected visible rate, including valid zero and negative results. */
function selectEtaRate(rates: LiveRateSnapshot | null | undefined, average: number | null, mode: LevelEtaMode):
  { etaRate: number | null; etaRateSource: LevelEtaMode | null } {
  const recent = rates?.expChart?.recent
  if (mode === 'realtime' && recent?.status === 'ready' && recent.value !== null && Number.isFinite(recent.value)) {
    return { etaRate: recent.value, etaRateSource: 'realtime' }
  }
  return average !== null && Number.isFinite(average)
    ? { etaRate: average, etaRateSource: 'average' }
    : { etaRate: null, etaRateSource: null }
}

/** One validity/hold policy for the dashboard and every widget renderer. */
export function selectExperienceReadout(rates: LiveRateSnapshot | null | undefined,
  session: SessionRecord | null | undefined, state: GameStateSnapshot | null | undefined, levelEtaMode: LevelEtaMode = 'realtime') {
  const partial = rates?.partialExp ?? session?.aggregate.partialExp
  if (partial) {
    const latestExp = partial.lastConfirmedExp ?? state?.exp
    const point = partial.waitingForLevel ? { ...latestExp, requiredExp: null, absolute: latestExp?.absolute ?? null, ratio: latestExp?.ratio ?? null }
      : rates?.expReadout?.lastConfirmed?.point ?? state?.exp
    const value = session?.aggregate.expPerHour ?? null
    return { value, ...selectEtaRate(rates, value, levelEtaMode), held: false, hidden: false, recovery: false, pending: false,
      point, level: partial.waitingForLevel ? null : state?.level, netGain: partial.gained, allowTrend: false }
  }
  const readout = rates?.expReadout
  const pending = rates?.expPerHour.calculationStatus === 'pending-transition'
    || rates?.expPerHour.calculationStatus === 'unresolved-level'
  const held = readout?.status === 'updating'
  const hidden = readout ? readout.status === 'stale' || readout.status === 'recovering' : pending
  const recovery = readout?.scope === 'recovery'
  const value = hidden ? null : held ? readout?.lastConfirmed?.value ?? null
    : recovery ? rates?.expRecovery?.value ?? null
      : rates?.expPerHour.status !== 'unavailable' ? rates?.expPerHour.value ?? null : null
  const point = readout ? readout.lastConfirmed?.point : state?.exp
  const level = readout ? readout.lastConfirmed?.point.level : state?.level
  return {
    value, ...(hidden ? { etaRate: null, etaRateSource: null } : selectEtaRate(rates, value, levelEtaMode)), held, hidden, recovery, pending,
    point, level,
    netGain: hidden || recovery ? null : held ? readout?.lastConfirmed?.netGain ?? null : session?.aggregate.expGained ?? null,
    allowTrend: session?.status === 'running' && !pending && !held && !hidden && !recovery,
  }
}
